/**
 * StorePage - Enhanced with Social Sharing & Open Graph Meta Tags
 * Public seller store page with custom branding and product display
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import ProductCard3D from '../components/ProductCard3D';
import { 
  ArrowLeft, 
  Store, 
  Star, 
  MapPin, 
  Phone, 
  Mail,
  Search,
  Filter,
  Grid3X3,
  LayoutList,
  ChevronDown,
  Heart,
  Share2,
  MessageCircle,
  Package,
  CheckCircle,
  Shield,
  Copy,
  ExternalLink,
  Facebook,
  Instagram
} from 'lucide-react';

// Social media icons
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Store theme styles
const THEME_STYLES = {
  // Jamaica colors
  green: { gradient: 'from-[#009B3A]/20 via-slate-900 to-slate-950', accent: '#009B3A', glow: 'shadow-[0_0_60px_rgba(0,155,58,0.3)]', border: 'border-[#009B3A]/30' },
  gold: { gradient: 'from-[#FCD116]/20 via-slate-900 to-slate-950', accent: '#FCD116', glow: 'shadow-[0_0_60px_rgba(252,209,22,0.3)]', border: 'border-[#FCD116]/30' },
  black: { gradient: 'from-slate-700/20 via-slate-900 to-slate-950', accent: '#333333', glow: 'shadow-[0_0_60px_rgba(100,100,100,0.2)]', border: 'border-slate-600' },
  // Bright colors
  red: { gradient: 'from-[#FF3B30]/20 via-slate-900 to-slate-950', accent: '#FF3B30', glow: 'shadow-[0_0_60px_rgba(255,59,48,0.3)]', border: 'border-[#FF3B30]/30' },
  orange: { gradient: 'from-[#FF9500]/20 via-slate-900 to-slate-950', accent: '#FF9500', glow: 'shadow-[0_0_60px_rgba(255,149,0,0.3)]', border: 'border-[#FF9500]/30' },
  yellow: { gradient: 'from-[#FFCC00]/20 via-slate-900 to-slate-950', accent: '#FFCC00', glow: 'shadow-[0_0_60px_rgba(255,204,0,0.3)]', border: 'border-[#FFCC00]/30' },
  lime: { gradient: 'from-[#34C759]/20 via-slate-900 to-slate-950', accent: '#34C759', glow: 'shadow-[0_0_60px_rgba(52,199,89,0.3)]', border: 'border-[#34C759]/30' },
  teal: { gradient: 'from-[#5AC8FA]/20 via-slate-900 to-slate-950', accent: '#5AC8FA', glow: 'shadow-[0_0_60px_rgba(90,200,250,0.3)]', border: 'border-[#5AC8FA]/30' },
  cyan: { gradient: 'from-[#32ADBC]/20 via-slate-900 to-slate-950', accent: '#32ADBC', glow: 'shadow-[0_0_60px_rgba(50,173,188,0.3)]', border: 'border-[#32ADBC]/30' },
  blue: { gradient: 'from-[#007AFF]/20 via-slate-900 to-slate-950', accent: '#007AFF', glow: 'shadow-[0_0_60px_rgba(0,122,255,0.3)]', border: 'border-[#007AFF]/30' },
  purple: { gradient: 'from-[#AF52DE]/20 via-slate-900 to-slate-950', accent: '#AF52DE', glow: 'shadow-[0_0_60px_rgba(175,82,222,0.3)]', border: 'border-[#AF52DE]/30' },
  pink: { gradient: 'from-[#FF2D55]/20 via-slate-900 to-slate-950', accent: '#FF2D55', glow: 'shadow-[0_0_60px_rgba(255,45,85,0.3)]', border: 'border-[#FF2D55]/30' },
  coral: { gradient: 'from-[#FF6F61]/20 via-slate-900 to-slate-950', accent: '#FF6F61', glow: 'shadow-[0_0_60px_rgba(255,111,97,0.3)]', border: 'border-[#FF6F61]/30' },
  mint: { gradient: 'from-[#00C7BE]/20 via-slate-900 to-slate-950', accent: '#00C7BE', glow: 'shadow-[0_0_60px_rgba(0,199,190,0.3)]', border: 'border-[#00C7BE]/30' },
  white: { gradient: 'from-slate-200/10 via-slate-900 to-slate-950', accent: '#666666', glow: 'shadow-[0_0_60px_rgba(255,255,255,0.1)]', border: 'border-slate-500' },
  // Legacy
  classic: { gradient: 'from-slate-800 via-slate-900 to-slate-950', accent: '#ffffff', glow: 'shadow-[0_0_60px_rgba(255,255,255,0.1)]', border: 'border-slate-600' },
  neon: { gradient: 'from-[#00A86B]/30 via-[#FFD300]/10 to-slate-950', accent: '#00A86B', glow: 'shadow-[0_0_80px_rgba(0,168,107,0.4)]', border: 'border-[#00A86B]/50' }
};

export default function StorePage() {
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState(null);
  const [business, setBusiness] = useState(null);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchStore = useCallback(async () => {
    try {
      const res = await api.get(`/stores/${merchantId}`);
      setStore(res.data.store);
      setBusiness(res.data.business);
      setFeaturedProducts(res.data.featured_products || []);
      setReviews(res.data.recent_reviews || []);
    } catch (error) {
      console.error('Error fetching store:', error);
      toast.error('Store not found');
      navigate('/shop');
    }
  }, [merchantId, navigate]);

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort_by: sortBy
      });
      
      if (selectedCategory) params.append('category', selectedCategory);
      
      const res = await api.get(`/stores/${merchantId}/products?${params}`);
      setProducts(res.data.products);
      setTotalPages(res.data.pages);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [merchantId, page, sortBy, selectedCategory]);

  useEffect(() => {
    const init = async () => {
      await fetchStore();
      await fetchProducts();
      setLoading(false);
    };
    init();
  }, [fetchStore, fetchProducts]);

  const handleAddToCart = async (productId) => {
    try {
      await api.post('/cart/add', { product_id: productId, quantity: 1 });
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  // Share functionality
  const getShareUrl = () => `${window.location.origin}/store/${merchantId}`;
  
  const getShareText = () => {
    const name = store?.store_name || business?.name || 'Store';
    return `Check out ${name} on Liantiao! 🛒 Great products from Jamaica 🇯🇲`;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast.success('Store link copied! Share it anywhere');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(getShareText() + '\n' + getShareUrl())}`;
    window.open(url, '_blank');
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: store?.store_name || business?.name,
          text: getShareText(),
          url: getShareUrl(),
        });
      } catch (err) {
        if (err.name !== 'AbortError') setShowShareDialog(true);
      }
    } else {
      setShowShareDialog(true);
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
  };

  const theme = store?.store_theme || 'green';
  const themeStyle = THEME_STYLES[theme];

  // Filter products by search
  const filteredProducts = products.filter(p => 
    !searchQuery || p.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Meta data for social sharing
  const storeName = store?.store_name || business?.name || 'Store';
  const storeDescription = store?.about || business?.description || `Shop from ${storeName} on Liantiao - Jamaica's Marketplace`;
  const storeImage = store?.store_logo || business?.logo || 'https://liantiaoja.com/default-store.png';
  const storeBanner = store?.store_banner;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00A86B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading store...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Open Graph Meta Tags for Social Sharing */}
      <Helmet>
        <title>{storeName} | Liantiao Jamaica</title>
        <meta name="description" content={storeDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getShareUrl()} />
        <meta property="og:title" content={`${storeName} - Shop on Liantiao`} />
        <meta property="og:description" content={storeDescription} />
        <meta property="og:image" content={storeBanner || storeImage} />
        <meta property="og:site_name" content="Liantiao Jamaica" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={getShareUrl()} />
        <meta name="twitter:title" content={`${storeName} - Shop on Liantiao`} />
        <meta name="twitter:description" content={storeDescription} />
        <meta name="twitter:image" content={storeBanner || storeImage} />
      </Helmet>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#00A86B]" />
              Share this Store
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Store Preview */}
            <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-xl">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-700">
                {storeImage ? (
                  <img src={storeImage} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Store className="w-8 h-8 text-slate-500" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-white">{storeName}</p>
                <p className="text-sm text-slate-400">{products.length} products</p>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-4 h-4 fill-[#FFD300] text-[#FFD300]" />
                  <span className="text-sm">{store?.rating_average?.toFixed(1) || '0.0'}</span>
                </div>
              </div>
            </div>
            
            {/* Share Options */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={shareToWhatsApp}
                className="flex items-center justify-center gap-3 p-4 rounded-xl bg-green-900/30 hover:bg-green-900/50 transition-colors text-green-400"
              >
                <WhatsAppIcon />
                <span className="font-medium">WhatsApp</span>
              </button>
              
              <button
                onClick={shareToFacebook}
                className="flex items-center justify-center gap-3 p-4 rounded-xl bg-blue-900/30 hover:bg-blue-900/50 transition-colors text-blue-400"
              >
                <Facebook className="w-6 h-6" />
                <span className="font-medium">Facebook</span>
              </button>
              
              <button
                onClick={copyLink}
                className="flex items-center justify-center gap-3 p-4 rounded-xl bg-pink-900/30 hover:bg-pink-900/50 transition-colors text-pink-400"
              >
                <TikTokIcon />
                <span className="font-medium">TikTok</span>
              </button>
              
              <button
                onClick={copyLink}
                className="flex items-center justify-center gap-3 p-4 rounded-xl bg-purple-900/30 hover:bg-purple-900/50 transition-colors text-purple-400"
              >
                <Instagram className="w-6 h-6" />
                <span className="font-medium">Instagram</span>
              </button>
            </div>
            
            {/* Copy Link */}
            <div className="flex items-center gap-2 p-3 bg-slate-800 rounded-xl">
              <input
                type="text"
                readOnly
                value={getShareUrl()}
                className="flex-1 bg-transparent text-sm text-slate-400 outline-none"
              />
              <Button
                onClick={copyLink}
                size="sm"
                className="bg-[#00A86B] hover:bg-[#00A86B]/90 text-white"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
            </div>
            
            <p className="text-xs text-center text-slate-500">
              Copy the link and paste it on any social media!
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#00A86B]" />
              Contact {storeName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Store Preview */}
            <div className="flex items-center gap-4 p-4 bg-slate-800 rounded-xl">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-700">
                {storeImage ? (
                  <img src={storeImage} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Store className="w-8 h-8 text-slate-500" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-white">{storeName}</p>
                <p className="text-sm text-slate-400">{business?.address || 'Jamaica'}</p>
              </div>
            </div>
            
            {/* In-App Chat Only - NEW Feature */}
            <div className="space-y-3">
              <button 
                onClick={() => {
                  if (!user) {
                    navigate("/login?redirect=" + encodeURIComponent(window.location.pathname));
                    return;
                  }
                  navigate("/messages?seller=" + (business?.id || storeId));
                  setShowContactModal(false);
                }}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 transition-all shadow-lg"
              >
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-white text-lg">Chat with Store</p>
                    <span className="px-2 py-0.5 bg-yellow-400 text-black text-xs font-bold rounded-full animate-pulse">NEW</span>
                  </div>
                  <p className="text-sm text-white/80">Secure in-app messaging</p>
                </div>
              </button>
              <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">Safe & Secure Communication</p>
                    <p className="text-xs text-slate-400 mt-1">All chats monitored. Contact sharing is against policy.</p>
                  </div>
                </div>
              </div>
              {business?.address && (
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Store Location</p>
                    <p className="text-sm text-slate-400">{business.address}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 3D Hero Banner */}
      <div 
        className={`relative overflow-hidden bg-gradient-to-b ${themeStyle.gradient}`}
        style={{ minHeight: '320px' }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-20 h-20 rounded-2xl bg-white/5 animate-float" style={{ animationDelay: '0s' }} />
          <div className="absolute top-20 right-20 w-16 h-16 rounded-full bg-white/5 animate-float" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-10 left-1/4 w-24 h-24 rounded-3xl bg-white/5 animate-float" style={{ animationDelay: '2s' }} />
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-30`} style={{ background: themeStyle.accent }} />
        </div>

        {/* Banner Image */}
        {store?.store_banner && (
          <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${store.store_banner})` }} />
        )}

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="absolute top-4 left-4 text-white/70 hover:text-white hover:bg-white/10">
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <div className="flex flex-col md:flex-row items-center gap-6 pt-12">
            {/* Logo */}
            <div className={`w-28 h-28 rounded-2xl bg-slate-800 ${themeStyle.border} border-2 ${themeStyle.glow} flex items-center justify-center overflow-hidden`} style={{ transform: 'perspective(500px) rotateY(-5deg)' }}>
              {business?.logo || store?.store_logo ? (
                <img src={store?.store_logo || business?.logo} alt={storeName} className="w-full h-full object-cover" />
              ) : (
                <Store className="w-12 h-12" style={{ color: themeStyle.accent }} />
              )}
            </div>

            {/* Store Details */}
            <div className="text-center md:text-left flex-1">
              <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                <h1 className="text-3xl font-bold">{storeName}</h1>
                {store?.rating_count > 10 && (
                  <Badge className="bg-[#00A86B]/20 text-[#00A86B] border border-[#00A86B]/30">
                    <Shield className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              <p className="text-slate-400 max-w-xl mb-4">{storeDescription}</p>

              <div className="flex items-center gap-6 justify-center md:justify-start text-sm">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-[#FFD300] text-[#FFD300]" />
                  <span className="font-medium">{store?.rating_average?.toFixed(1) || '0.0'}</span>
                  <span className="text-slate-400">({store?.rating_count || 0} reviews)</span>
                </div>
                <div className="flex items-center gap-1 text-slate-400">
                  <Package className="w-4 h-4" />
                  <span>{products.length} products</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="outline" size="icon" className={`border-slate-600 ${isFavorite ? 'text-red-400' : ''}`} onClick={toggleFavorite}>
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
              </Button>
              <Button variant="outline" size="icon" className="border-slate-600" onClick={shareNative} data-testid="share-store-btn">
                <Share2 className="w-4 h-4" />
              </Button>
              <Button className="bg-[#00A86B] hover:bg-[#00A86B]/90" onClick={() => setShowContactDialog(true)}>
                <MessageCircle className="w-4 h-4 mr-2" />
                Contact
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#FFD300]" />
            Featured Products
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
            {featuredProducts.map(product => (
              <ProductCard3D key={product.id} product={product} theme={theme} onAddToCart={handleAddToCart} />
            ))}
          </div>
        </section>
      )}

      {/* Products Section */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-slate-800/50 border-slate-700" />
          </div>

          {categories.length > 0 && (
            <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
              <option value="">All Categories</option>
              {categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
            </select>
          )}

          <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
            <option value="newest">Newest</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="rating">Best Rated</option>
            <option value="best_selling">Best Selling</option>
          </select>

          <div className="flex border border-slate-700 rounded-lg overflow-hidden">
            <Button variant="ghost" size="icon" className={viewMode === 'grid' ? 'bg-slate-700' : ''} onClick={() => setViewMode('grid')}><Grid3X3 className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className={viewMode === 'list' ? 'bg-slate-700' : ''} onClick={() => setViewMode('list')}><LayoutList className="w-4 h-4" /></Button>
          </div>
        </div>

        {filteredProducts.length > 0 ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 justify-items-center' : 'flex flex-col gap-4'}>
            {filteredProducts.map(product => (
              <ProductCard3D key={product.id} product={product} theme={theme} onAddToCart={handleAddToCart} size={viewMode === 'list' ? 'large' : 'default'} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No products found</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-slate-700">Previous</Button>
            <span className="flex items-center px-4 text-slate-400">Page {page} of {totalPages}</span>
            <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-slate-700">Next</Button>
          </div>
        )}
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8 border-t border-slate-800">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#00A86B]" />
            Customer Reviews
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviews.map(review => (
              <Card key={review.id} className="p-4 bg-slate-800/50 border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                    <span className="font-medium">{review.reviewer_name?.[0] || 'C'}</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">{review.reviewer_name}</p>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star key={star} className={`w-3 h-3 ${star <= review.rating ? 'fill-[#FFD300] text-[#FFD300]' : 'text-slate-600'}`} />
                      ))}
                    </div>
                  </div>
                  {review.is_verified_purchase && (
                    <Badge className="ml-auto bg-green-500/10 text-green-400 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />Verified
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-300">{review.comment}</p>
                {review.photos?.length > 0 && (
                  <div className="flex gap-2 mt-3">
                    {review.photos.slice(0, 3).map((photo, i) => (
                      <img key={i} src={photo} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>
      )}

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
